/**
 * EvolveRPG homepage copy. The landing page itself is evolverpg's own
 * (src/home/EvolveHomePage.tsx, the `homePage` world slot); the shared
 * section keys further down still voice any shared component that reads them.
 *
 * Rules for this copy: EvolveRPG is a rules system and this site is its demo.
 * Say only what is true today. The rules are an unapproved draft, so describe
 * the zero start and the die ladder, never exact numbers. No em dashes.
 */
export const homeEvoEn = {
  'home.seoTitle': 'Play EvolveRPG',
  'home.seoDescription':
    'EvolveRPG is a 3D roleplaying game where every character starts at zero. Walk through Montkelian, a living city in real time, or take a party into The Portal, a turn-based boss battle. Free with an account.',
  'home.heroTitle': 'Every legend starts at zero.',
  'home.heroBody':
    'EvolveRPG is a 3D roleplaying game. Every character begins with a d4 and six tracks at zero. Your character grows from what they do in play.',
  'home.heroPlayCta': 'Play now',
  'home.heroQuickstartPrompt':
    "The tabletop rules are still being written. Join the list and we'll email you the free Quickstart when it is ready.",

  // EvolveHomePage
  'home.evo.heroArtAlt': 'EvolveRPG: start with nothing, become anything. A traveller on a cliff looks over a river valley toward a city at sunrise, beside three coins of leaf, crystal and flame.',
  'home.evo.play.new': 'Free account, then you make your character.',
  'home.evo.play.make': 'You are signed in. Make your character, then walk into Montkelian.',
  'home.evo.play.resume': 'Your character is waiting in Montkelian.',
  'home.evo.modes.title': 'Ways to play',
  'home.evo.modes.body': 'The city uses your own EvolveRPG character. The Portal gives you a ready-made level 7 fighter.',
  'home.evo.modes.pageTitle': 'Play EvolveRPG',
  'home.evo.modes.pick': 'How do you want to play?',
  'home.evo.modes.pickBody': 'Montkelian uses your own EvolveRPG character. The Portal gives you a ready-made level 7 fighter. Come back here to switch.',
  'home.evo.mode.city.kind': 'Live city, first person or overhead',
  'home.evo.mode.city.title': 'Montkelian',
  'home.evo.mode.city.body': 'Walk the capital with whoever else is online. The townspeople keep their own routines, and the city runs on its own clock. Play through your own eyes or from overhead, and switch any time with the view button or the P key.',
  'home.evo.mode.city.cta': 'Enter Montkelian',
  'home.evo.mode.vtt.kind': 'VTT, turn based',
  'home.evo.mode.vtt.title': 'The Portal',
  'home.evo.mode.vtt.body': 'Miniatures on a tabletop. Roll initiative and move a party of four square by square against the Riftmaw, a beast being summoned at an altar, using SRD 5.2.1 rules. Give orders by clicking, typing, or by voice in Chrome and Edge.',
  'home.evo.mode.vtt.cta': 'Play turn based',
  'home.evo.city.title': 'Montkelian, the living city',
  'home.evo.city.kind': 'Open world, real time',
  'home.evo.city.body':
    'A grey, rain-heavy capital where the townspeople keep a weekly routine. Walk the streets, follow people, talk with them, and watch their day carry on without you.',
  'home.evo.city.cta': 'Enter Montkelian',
  'home.evo.city.note': 'Needs a free account.',
  'home.evo.portal.title': 'The Portal',
  'home.evo.portal.kind': 'New: turn-based boss battle',
  'home.evo.portal.body':
    'The Riftmaw is being summoned at an altar, with its keepers and brutes around it. Roll initiative and move a party of four square by square: a ready-made level 7 fighter, Tuck the dog, and Wren and Sideguard Brann from Montkelian. Uses SRD 5.2.1 rules by Wizards of the Coast LLC (CC BY 4.0). Give orders by clicking, typing, or by voice in Chrome and Edge.',
  'home.evo.portal.cta': 'Play The Portal',
  'home.evo.portal.note': 'Needs a free account. New, so the fight is not balanced yet.',
  'home.evo.rulesCta': 'Get the Quickstart',
  'home.evo.legend.title': 'How zero becomes a legend',
  'home.evo.legend.ladderTitle': 'The die steps up',
  'home.evo.legend.ladderBody':
    'Everyone starts on a d4. As your character grows, their die steps up to a bigger one.',
  'home.evo.legend.ladderAria': 'The die ladder, from the starting d4 upward',
  'home.evo.legend.ladderMore': 'and larger dice beyond',
  'home.evo.legend.tracksTitle': 'Six tracks, all at zero',
  'home.evo.legend.tracksBody':
    'Physical, Mental, Spiritual, Emotional, Social and Temporal. The tracks you use climb. The ones you ignore stay where they are.',
  'home.evo.legend.trackAt': '{track}: starts at 0',
  'home.evo.track.physical': 'Physical',
  'home.evo.track.mental': 'Mental',
  'home.evo.track.spiritual': 'Spiritual',
  'home.evo.track.emotional': 'Emotional',
  'home.evo.track.social': 'Social',
  'home.evo.track.temporal': 'Temporal',
  'home.evo.quickstart.title': 'Get the free tabletop Quickstart',
  'home.evo.quickstart.cta': 'Join the list',
  'home.evo.quickstart.privacy': 'Privacy Policy',

  // TodayInGrymwyrd
  'home.today.heading': 'Today in EvolveRPG',

  // LiveFeed
  'home.liveFeed.heading': 'Live From the Table',
  'home.liveFeed.badge': 'fresh updates',
  'home.liveFeed.loading': 'Catching up on the action…',
  'home.liveFeed.actorFallback': 'A player',
  'home.liveFeed.actionFallback': 'made a move',
  'home.liveFeed.labelBestiary': 'New codex entry',
  'home.liveFeed.labelBook': 'Content updated',
  'home.liveFeed.empty': 'Quiet at the table right now. Check back once the next session wraps.',

  // FeaturedBooks
  'home.featuredBooks.heading': 'Featured Books',
  'home.featuredBooks.loading': 'Pulling up the playbook…',
  'home.featuredBooks.descriptionFallback': 'A ready-to-play book packed with rules, moves, and genre content.',

  // QuickTools
  'home.quickTools.arsenalTitle': 'The Full Toolkit',
  'home.quickTools.arsenalBody':
    'Character sheets, the codex, the dice roller, and the live feed. Everything you need to keep the session moving.',
  'home.quickTools.encounterTitle': 'Quick Encounter',
  'home.quickTools.encounterBody': 'Roll the dice and let momentum decide what shows up next.',
  'home.quickTools.encounterResultHeading': 'Encounter Rolled',
  'home.quickTools.encounterSilent': 'The dice are quiet',
  'home.quickTools.encounterSummoning': 'Rolling…',
  'home.quickTools.encounterEmpty': 'Nothing came up. Roll again.',
  'home.quickTools.clockTitle': 'Campaign Clock',
  'home.quickTools.clockYear': 'Year One of the Evolution',
  'home.quickTools.clockAnno': 'Cycle {year}',
}
